import React, { useState, useEffect } from "react";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Toolbar,
  Edit,
  Inject,
  Filter,
} from "@syncfusion/ej2-react-grids";
import { toast } from "react-hot-toast";
import { IoCloudDownload, IoArrowBack } from "react-icons/io5";
import { Link, useNavigate } from "react-router-dom";
import { getEducationData } from "../api/Api";
import { useAuth } from "../contexts/AuthContext";
import NavBar from "../components/Navbar/NavBar";

function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(""); // initially none selected
  const { userData } = useAuth();

  const loadData = () => {
    setLoading(true);
    getEducationData()
      .then((res) => {
        toast.success(res.data.message);
        const fetchedData = res.data.data;
        setData(fetchedData);

        // Extract unique plan names including blanks
        const uniquePlans = [
          ...new Set(fetchedData.map((item) => item.planName || "")),
        ].sort();
        setAllPlans(uniquePlans);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(error);
        toast.error("Failed to load data");
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  const actionTemplate = (props) => {
    return (
      <Link
        to={{
          pathname: "/viewOrderList",
        }}
        state={{
          name: props.name,
          email: props.email,
        }}
        exact
      >
        <button
          className="px-2 text-sm border-1 border-brightColor bg-backgroundColor hover:text-brightColor transition-all rounded-lg font-bold uppercase"
          type="button"
        >
          <IoCloudDownload />
        </button>
      </Link>
    );
  };

  const filteredData = data.filter(
    (item) => (item.planName || "") === selectedPlan
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <NavBar />
      <div className="pt-28 pb-16 px-4 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/")}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <IoArrowBack className="mr-1" />
            Back
          </button>
          <h2 className="text-2xl font-semibold text-gray-800">
            Admin Dashboard
          </h2>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <h3 className="font-semibold mb-3">Select a Plan:</h3>
          <div className="flex flex-wrap gap-3">
            {allPlans.map((plan) => (
              <button
                key={plan || "admin"}
                className={`px-4 py-2 text-sm rounded-full font-medium transition-all border ${
                  selectedPlan === plan
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-blue-100"
                }`}
                onClick={() => setSelectedPlan(plan)}
              >
                {plan === "" ? "ADMIN" : plan}
              </button>
            ))}
          </div>
        </div>

        { filteredData.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <GridComponent
              dataSource={filteredData}
              allowPaging={true}
              pageSettings={{ pageCount: 5 }}
              toolbar={["Search"]}
              allowFiltering={true}
              filterSettings={{
                type: "Menu",
                mode: "Immediate",
                immediateModeDelay: 0,
              }}
            >
              <ColumnsDirective>
                <ColumnDirective
                  field="name"
                  headerText="Name"
                  width="150"
                  textAlign="Center"
                  isPrimaryKey={true}
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="email"
                  headerText="Email"
                  width="150"
                  textAlign="Center"
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="mobile"
                  headerText="Mobile No"
                  width="150"
                  textAlign="Center"
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="planName"
                  headerText="Plan Name"
                  width="150"
                  textAlign="Center"
                  allowFiltering={false}
                  template={(props) => (
                    <span>{!props.planName ? "ADMIN" : props.planName}</span>
                  )}
                />
                <ColumnDirective
                  field="planCost"
                  headerText="Plan Cost"
                  width="150"
                  textAlign="Center"
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="planDuration"
                  headerText="Plan Duration"
                  width="150"
                  textAlign="Center"
                  allowFiltering={false}
                />
                <ColumnDirective
                  field="paymentStatus"
                  headerText="Payment Status"
                  width="150"
                  textAlign="Center"
                  template={actionTemplate}
                  allowFiltering={false}
                />
              </ColumnsDirective>
              <Inject services={[Page, Toolbar, Edit, Filter]} />
            </GridComponent>
          </div>
        )}

        {selectedPlan && filteredData.length === 0 && (
          <div className="text-center text-gray-500 text-md mt-6">
            No data available for the selected plan.
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
